/**
 * `/settings` — "Cấu hình công ty", Task CS-2.
 *
 * Two choices and two facts, on one sheet of paper. The choices — the country this
 * company operates in and the currency it pays in — have decided how a payslip is
 * labelled and which country's rules the AI is instructed to follow since Task CS-1,
 * and until this screen they could only be changed with a database client. The facts
 * — múi giờ and ngôn ngữ — are written by the achau mirror and are here to be read,
 * never to be typed into: the `PUT` body is `.strict()`, and a form that offered them
 * would be building a refusal.
 *
 * ── The currency is suggested, then it is theirs ────────────────────────────
 *
 * Picking a country pre-selects the currency that country is usually paid in, and
 * **stops** the moment the person picks a currency themselves. A company registered
 * in Singapore that pays in dollars is ordinary; a form that re-imposed SGD on every
 * later country change would be arguing with a fact it does not know.
 *
 * ── What this screen must not let a reader believe ──────────────────────────
 *
 * Choosing Thailand does not make the payroll engine Thai. The engine is Vietnamese
 * statute — the progressive PIT tariff, the BHXH/BHYT/BHTN caps — and CS-1 left it
 * untouched to the line; there is no exchange rate anywhere in the service and no
 * stored figure is ever restated. That ceiling is on screen, in three sentences, as a
 * quiet note rather than a warning banner: it is a fact about the product, not a
 * problem the reader caused, and colouring it red would send somebody looking for
 * something to fix.
 *
 * ── The nine states, and the one that is absent ─────────────────────────────
 *
 * Skeleton while the read is in flight; `ApiErrorNote` for the read and again for the
 * write, which routes a `403` to `PermissionDenied` and a retryable failure to
 * `RetryAction` on its own. **No `LastSync`**: that component states when a
 * background pipeline last delivered data, and these two columns are written by a
 * person pressing a button in this form — `updated_at` is shown as the fact it is,
 * beside who the row belongs to, not as a freshness stamp for a feed that does not
 * exist.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { formatInstant, timezoneName } from '../i18n/datetime';
import {
  COMPANY_SETTINGS_PATH,
  useCompanySettings,
  type CompanySettings,
  type CompanySettingsUpdateRequest,
} from '../api/company-settings';
import { useApiMutation } from '../api/useApiMutation';
import {
  COUNTRIES,
  CURRENCIES,
  defaultCurrencyFor,
  localeName,
} from '../company/regions';
import { refusedFields } from '../policies/diff';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import { Icon } from '../states/icons';
import { ScreenHeading } from '../shell/ScreenHeading';

export function CompanySettingsScreen(): ReactNode {
  const { t, locale } = useI18n();
  const settings = useCompanySettings();
  const save = useApiMutation<CompanySettingsUpdateRequest, CompanySettings>(
    COMPANY_SETTINGS_PATH,
    'PUT',
  );

  /**
   * What the person has changed, and nothing else.
   *
   * A draft rather than two `useState`s seeded from the response: seeding needs an
   * effect that fires when the read lands, and an effect that writes form state is
   * the thing that overwrites somebody's half-made choice when the write's own
   * response arrives. Absent means "whatever the server last told us".
   */
  const [draftCountry, setDraftCountry] = useState<string | null>(null);
  const [draftCurrency, setDraftCurrency] = useState<string | null>(null);
  /**
   * Has the person chosen the currency **themselves**?
   *
   * Its own flag rather than `draftCurrency !== null`, which is what the first
   * version used and is wrong: the country's own suggestion fills `draftCurrency`,
   * so the second country change would find it non-null and conclude the person had
   * chosen — leaving somebody who picked Thailand and then Japan looking at baht.
   */
  const [currencyChosen, setCurrencyChosen] = useState(false);

  /** The row on screen: the write's answer when there has been one, else the read's. */
  const row = save.data ?? settings.data;
  const country = draftCountry ?? row?.country_code ?? '';
  const currency = draftCurrency ?? row?.currency_code ?? '';

  /** The fields the server named in its refusal — never a guess made here. */
  const refused = save.error === null ? [] : refusedFields(save.error.details);
  const invalid = (field: string): boolean => refused.some((name) => name.endsWith(field));

  const dirty = row !== null && (country !== row.country_code || currency !== row.currency_code);

  const submit = (): void => {
    if (country === '' || currency === '') return;
    void save.submit({ country_code: country, currency_code: currency });
  };

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('companySettings.title')}</ScreenHeading>

      {settings.status === 'loading' ? (
        <Skeleton
          rows={4}
          variant="block"
          label={t('companySettings.subject')}
          testId="cs2-skeleton"
        />
      ) : null}

      {settings.status === 'error' && settings.error !== null ? (
        <ApiErrorNote
          error={settings.error}
          onRetry={settings.reload}
          subject={t('companySettings.subject')}
          testId="cs2-error"
        />
      ) : null}

      {row === null ? null : (
        <form
          className="wp-section wp-settings"
          data-testid="cs2-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <h2 className="wp-section__title">{t('companySettings.region.title')}</h2>

          <div className="wp-settings__pair">
            <div className="wp-field">
              <label className="wp-field__label" htmlFor="cs2-country">
                {t('companySettings.country')}
              </label>
              <div className="wp-select">
                <select
                  className="wp-field__input"
                  id="cs2-country"
                  data-testid="cs2-country"
                  value={country}
                  aria-invalid={invalid('country_code')}
                  onChange={(event) => {
                    const chosen = event.target.value;
                    setDraftCountry(chosen);
                    // A suggestion, and only while they have not chosen for
                    // themselves. See the header.
                    if (!currencyChosen) {
                      const suggested = defaultCurrencyFor(chosen);
                      if (suggested !== null) setDraftCurrency(suggested);
                    }
                  }}
                >
                  {COUNTRIES.map((entry) => (
                    <option key={entry.code} value={entry.code}>
                      {locale === 'en' ? entry.en : entry.vi}
                    </option>
                  ))}
                </select>
                <Icon name="chevron-down" className="wp-select__chevron" />
              </div>
            </div>

            <div className="wp-field">
              <label className="wp-field__label" htmlFor="cs2-currency">
                {t('companySettings.currency')}
              </label>
              <div className="wp-select">
                <select
                  className="wp-field__input"
                  id="cs2-currency"
                  data-testid="cs2-currency"
                  value={currency}
                  aria-invalid={invalid('currency_code')}
                  onChange={(event) => {
                    setDraftCurrency(event.target.value);
                    setCurrencyChosen(true);
                  }}
                >
                  {CURRENCIES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
                <Icon name="chevron-down" className="wp-select__chevron" />
              </div>
              <p className="wp-small wp-muted">{t('companySettings.currency.hint')}</p>
            </div>
          </div>

          {/* Quiet, and deliberately not a warning: the reader has not done anything
              wrong, and a red banner would send them looking for a fix. */}
          <p className="wp-small wp-muted wp-settings__note" data-testid="cs2-engine-note">
            {t('companySettings.engineNote')}
          </p>

          <dl className="wp-settings__facts">
            <div data-testid="cs2-timezone">
              <dt className="wp-label">{t('companySettings.timezone')}</dt>
              <dd>{timezoneName(row.timezone, locale)}</dd>
            </div>
            <div data-testid="cs2-locale">
              <dt className="wp-label">{t('companySettings.locale')}</dt>
              <dd>{localeName(row.locale, locale)}</dd>
            </div>
            <div data-testid="cs2-updated">
              <dt className="wp-label">{t('companySettings.updated')}</dt>
              <dd>{formatInstant(row.updated_at, locale, row.timezone)}</dd>
            </div>
          </dl>
          <p className="wp-small wp-muted">{t('companySettings.facts.hint')}</p>

          <div className="wp-settings__bar">
            <button
              type="submit"
              className="wp-button wp-button--primary"
              data-testid="cs2-save"
              disabled={save.status === 'pending'}
            >
              {save.status === 'pending' ? t('companySettings.saving') : t('companySettings.save')}
            </button>
            {dirty ? (
              <span className="wp-small wp-muted" data-testid="cs2-dirty">
                {t('companySettings.unsaved')}
              </span>
            ) : null}
          </div>

          {save.error === null ? null : (
            <ApiErrorNote
              error={save.error}
              subject={t('companySettings.subject')}
              testId="cs2-save-error"
            />
          )}

          {save.status === 'ok' && !dirty ? (
            <StatusNote severity="low" title={t('companySettings.saved')} testId="cs2-saved" />
          ) : null}
        </form>
      )}
    </div>
  );
}
